import { TFunction } from 'i18next'
import { z } from 'zod'

import { ZIP_CODE_CA_REGEX, ZIP_CODE_US_REGEX } from '@/constants/regex'
import { Building } from '@/reducers/types'
import { isValidLink } from '@/utils/link'
import { formatPhoneNumberDisplay } from '@/utils/phone-number'

import { isValidPhoneInput } from '../../../../shared/person-table.utils'
import { PLACE_LINK_MAX_LENGTH } from '../../../place-form-modal/place-form-modal.constants'
import { getServerTags } from '../../../place-payload'
import { IS_CONVENIENCE_FEE_ENABLED } from '../../../places.gates'
import { PLACE_TIMEZONES } from '../../../places.timezones'
import { isCanadaCountry } from '../../../places.utils'

export const EMERGENCY_PHONE_MAX_LENGTH = 15

const EMERGENCY_PHONE_REGEX = new RegExp(`^\\d{0,${EMERGENCY_PHONE_MAX_LENGTH}}$`)

export const PLACE_TEXT_MAX_LENGTH = 255

export const PLACE_GENERAL_FLAG_KEYS = [
  'active',
  'accountPastDue',
  'accountSuspended',
  'isHoa',
  'avatarChangeRestricted',
  'nameChangeRestricted',
] as const

export const PLACE_GENERAL_TEXT_KEYS = [
  'name',
  'address',
  'city',
  'state',
  'postalCode',
  'email',
  'link',
  'appName',
  'iosAppStoreLink',
  'googlePlayLink',
  'payRentLink',
  'maintenanceRequestLink',
  'emergencyPhone',
  'hubspotDealId',
] as const

export type PlaceGeneralFlagKey = (typeof PLACE_GENERAL_FLAG_KEYS)[number]

// Note: plain building fields, not features - they skip the feature rules of the flag rows
export const PLACE_GENERAL_SWITCH_KEYS = [
  'eventConvenienceFeeAvailable',
  'reservationConvenienceFeeAvailable',
] as const

export type PlaceGeneralSwitchKey = (typeof PLACE_GENERAL_SWITCH_KEYS)[number]

export const CONVENIENCE_FEE_RATE_MAX = 100

const optionalLink = (t: TFunction<['organisation']>) =>
  z
    .string()
    .trim()
    .max(
      PLACE_LINK_MAX_LENGTH,
      t('organisation:places.create.validation.linkMaxLength', { max: PLACE_LINK_MAX_LENGTH }),
    )
    .optional()
    .refine(
      (value) => !value || isValidLink(value),
      t('organisation:places.create.validation.linkInvalid'),
    )

const textMaxLength = (t: TFunction<['organisation']>) =>
  t('organisation:places.general.validation.textMaxLength', { max: PLACE_TEXT_MAX_LENGTH })

export const makePlaceGeneralSchema = (t: TFunction<['organisation']>, country: string) =>
  z
    .object({
      accountPastDue: z.boolean(),
      accountSuspended: z.boolean(),
      active: z.boolean(),
      address: z
        .string()
        .trim()
        .min(1, t('organisation:places.create.validation.addressRequired'))
        .max(PLACE_TEXT_MAX_LENGTH, textMaxLength(t)),
      appName: z.string().max(PLACE_TEXT_MAX_LENGTH, textMaxLength(t)).optional(),
      avatarChangeRestricted: z.boolean(),
      buildingResidence: z
        .string()
        .trim()
        .optional()
        .refine(
          (value) => !value || /^\d+$/.test(value),
          t('organisation:places.general.validation.residentsInvalid'),
        ),
      city: z.string().max(PLACE_TEXT_MAX_LENGTH, textMaxLength(t)).optional(),
      code: z.string().trim().min(1, t('organisation:places.create.validation.codeRequired')),
      email: z
        .string()
        .trim()
        .max(PLACE_TEXT_MAX_LENGTH, textMaxLength(t))
        .optional()
        .refine(
          (value) => !value || z.email().safeParse(value).success,
          t('organisation:places.create.validation.emailInvalid'),
        ),
      emergencyPhone: z
        .string()
        .trim()
        .optional()
        .refine(
          (value) => !value || EMERGENCY_PHONE_REGEX.test(value),
          t('organisation:places.general.validation.emergencyPhoneInvalid'),
        ),
      eventConvenienceFeeAvailable: z.boolean(),
      googlePlayLink: optionalLink(t),
      hubspotDealId: z.string().max(PLACE_TEXT_MAX_LENGTH, textMaxLength(t)).optional(),
      image: z.array(z.instanceof(File)).optional(),
      iosAppStoreLink: optionalLink(t),
      isHoa: z.boolean(),
      link: optionalLink(t),
      logo: z.array(z.instanceof(File)).optional(),
      maintenanceRequestLink: z.string().max(PLACE_TEXT_MAX_LENGTH, textMaxLength(t)).optional(),
      name: z
        .string()
        .trim()
        .min(1, t('organisation:places.create.validation.nameRequired'))
        .max(PLACE_TEXT_MAX_LENGTH, textMaxLength(t)),
      nameChangeRestricted: z.boolean(),
      payRentLink: z.string().max(PLACE_TEXT_MAX_LENGTH, textMaxLength(t)).optional(),
      phone: z
        .string()
        .min(1, t('organisation:places.create.validation.phoneRequired'))
        .refine(isValidPhoneInput, t('organisation:places.create.validation.phoneInvalid')),
      postalCode: z
        .string()
        .trim()
        .min(1, t('organisation:places.create.validation.postalCodeRequired')),
      reservationConvenienceFeeAvailable: z.boolean(),
      residentConvenienceFeeRate: z
        .string()
        .optional()
        .refine(
          (value) => !value || (Number(value) > 0 && Number(value) <= CONVENIENCE_FEE_RATE_MAX),
          t('organisation:places.general.validation.convenienceFeeRateInvalid'),
        ),
      state: z.string().nullish(),
      tags: z.array(z.string()),
      timezone: z.enum(Object.values(PLACE_TIMEZONES) as [string, ...string[]]),
    })
    .superRefine((data, ctx) => {
      if (!data.postalCode) return

      const isCanada = isCanadaCountry(country)
      const isValid = isCanada
        ? ZIP_CODE_CA_REGEX.test(data.postalCode)
        : ZIP_CODE_US_REGEX.test(data.postalCode)

      if (!isValid) {
        ctx.addIssue({
          code: 'custom',
          message: isCanada
            ? t('organisation:places.create.validation.postalCodeInvalidCa')
            : t('organisation:places.create.validation.postalCodeInvalidUs'),
          path: ['postalCode'],
        })
      }
    })

export type PlaceGeneralFormData = z.infer<ReturnType<typeof makePlaceGeneralSchema>>

const text = (value: unknown) => (value === null || value === undefined ? '' : String(value))

export const getPlaceGeneralDefaults = (building: Building): PlaceGeneralFormData => ({
  accountPastDue: !!building.accountPastDue,
  accountSuspended: !!building.accountSuspended,
  active: building.active !== false,
  address: text(building.address),
  appName: text(building.appName),
  avatarChangeRestricted: !!building.avatarChangeRestricted,
  buildingResidence: text(building.buildingResidence),
  city: text(building.city),
  code: text(building.code),
  email: text(building.email),
  emergencyPhone: text(building.emergencyPhone),
  eventConvenienceFeeAvailable: !!building.eventConvenienceFeeAvailable,
  googlePlayLink: text(building.googlePlayLink),
  hubspotDealId: text(building.hubspotDealId),
  image: [],
  iosAppStoreLink: text(building.iosAppStoreLink),
  isHoa: !!building.isHoa,
  link: text(building.link),
  logo: [],
  maintenanceRequestLink: text(building.maintenanceRequestLink),
  name: text(building.name),
  nameChangeRestricted: !!building.nameChangeRestricted,
  payRentLink: text(building.payRentLink),
  phone: formatPhoneNumberDisplay(building.phone),
  postalCode: text(building.postalCode),
  reservationConvenienceFeeAvailable: !!building.reservationConvenienceFeeAvailable,
  // Note: the backend stores 0 for "no fee", the form shows it as empty
  residentConvenienceFeeRate: building.residentConvenienceFeeRate
    ? String(building.residentConvenienceFeeRate)
    : '',
  state: text(building.state),
  tags: getServerTags(building),
  timezone: text(building.timezone) || PLACE_TIMEZONES.CT,
})

export type PlaceGeneralRowId =
  | PlaceGeneralFlagKey
  | PlaceGeneralSwitchKey
  | (typeof PLACE_GENERAL_TEXT_KEYS)[number]
  | 'residentConvenienceFeeRate'
  | 'buildingResidence'
  | 'code'
  | 'country'
  | 'image'
  | 'logo'
  | 'phone'
  | 'tags'
  | 'timezone'

export const PlaceGeneralGroupSchema = z.enum([
  'building',
  'location',
  'contact',
  'residents',
  'organisation',
  'branding',
  'convenienceFee',
])

export type PlaceGeneralGroup = z.infer<typeof PlaceGeneralGroupSchema>

export interface PlaceGeneralLayoutGroup {
  group: PlaceGeneralGroup
  rows: PlaceGeneralRowId[]
}

// Note: most important first; every required field sits in the first three groups
export const PLACE_GENERAL_LAYOUT: PlaceGeneralLayoutGroup[] = [
  {
    group: 'building',
    rows: ['name', 'code', 'active', 'accountPastDue', 'accountSuspended'],
  },
  {
    group: 'location',
    rows: ['address', 'city', 'state', 'postalCode', 'country', 'timezone'],
  },
  {
    group: 'contact',
    rows: ['email', 'phone', 'emergencyPhone', 'link'],
  },
  {
    group: 'residents',
    rows: ['buildingResidence', 'isHoa', 'avatarChangeRestricted', 'nameChangeRestricted'],
  },
  {
    group: 'organisation',
    rows: ['tags', 'hubspotDealId'],
  },
  {
    group: 'branding',
    rows: [
      'logo',
      'image',
      'appName',
      'maintenanceRequestLink',
      'payRentLink',
      'iosAppStoreLink',
      'googlePlayLink',
    ],
  },
  {
    group: 'convenienceFee',
    rows: [
      'residentConvenienceFeeRate',
      'eventConvenienceFeeAvailable',
      'reservationConvenienceFeeAvailable',
    ],
  },
]

export const PLACE_GENERAL_REQUIRED_ROWS: readonly PlaceGeneralRowId[] = [
  'name',
  'code',
  'address',
  'postalCode',
  'phone',
  'timezone',
]

export const PLACE_GENERAL_ADMIN_ROWS: readonly PlaceGeneralRowId[] = ['hubspotDealId']

export const PLACE_GENERAL_NUMERIC_ROWS: readonly PlaceGeneralRowId[] = [
  'code',
  'buildingResidence',
  'emergencyPhone',
]

export const PLACE_GENERAL_LINK_ROWS: readonly PlaceGeneralRowId[] = [
  'link',
  'iosAppStoreLink',
  'googlePlayLink',
  'payRentLink',
  'maintenanceRequestLink',
]

export type PlaceGeneralTextRow = Exclude<
  PlaceGeneralRowId,
  | PlaceGeneralFlagKey
  | PlaceGeneralSwitchKey
  | 'country'
  | 'image'
  | 'logo'
  | 'phone'
  | 'residentConvenienceFeeRate'
  | 'state'
  | 'tags'
  | 'timezone'
>

const PLACE_GENERAL_LABEL_KEYS: Partial<Record<PlaceGeneralRowId, string>> = {
  country: 'organisation:places.create.country.label',
  image: 'organisation:places.building.buildingPhoto',
  logo: 'organisation:places.building.logotype',
  timezone: 'organisation:places.building.timezone.label',
}

// Note: i18n keys are strictly typed, the cast covers the dynamic per-row label keys
export const getPlaceGeneralRowLabelKey = (row: PlaceGeneralRowId) =>
  (PLACE_GENERAL_LABEL_KEYS[row] ??
    `organisation:places.building.fields.${row}.label`) as 'organisation:places.building.logotype'

export const PLACE_GENERAL_PHONE_ROWS: readonly PlaceGeneralRowId[] = ['phone', 'emergencyPhone']

export const PLACE_GENERAL_PHOTO_ROWS: readonly PlaceGeneralRowId[] = ['logo', 'image']

export const PLACE_GENERAL_FLAG_ROWS: readonly PlaceGeneralRowId[] = PLACE_GENERAL_FLAG_KEYS

export const PLACE_GENERAL_SWITCH_ROWS: readonly PlaceGeneralRowId[] = PLACE_GENERAL_SWITCH_KEYS

export const getPlaceGeneralLayout = (isAdmin: boolean): PlaceGeneralLayoutGroup[] =>
  PLACE_GENERAL_LAYOUT.filter(
    ({ group }) => IS_CONVENIENCE_FEE_ENABLED || group !== 'convenienceFee',
  )
    .map(({ group, rows }) => ({
      group,
      rows: rows.filter((row) => isAdmin || !PLACE_GENERAL_ADMIN_ROWS.includes(row)),
    }))
    .filter(({ rows }) => rows.length > 0)
